/**
 * 请求锁使用的可替换时钟，生产环境使用系统时间，测试可注入可控时钟。
 */
export interface RequestWriteLockClock {
    now: () => number;
    setTimeout: (callback: () => void, delay: number) => unknown;
    clearTimeout: (timerId: unknown) => void;
}

/**
 * 请求锁创建参数。
 */
export interface RequestWriteLockOptions {
    clock?: RequestWriteLockClock;
    minLockMs?: number;
    maxLockMs?: number;
}

/**
 * 生成请求键所需的传输层字段。
 */
export interface WriteRequestKeyConfig {
    baseURL?: string;
    data?: unknown;
    dedupe?: boolean;
    dedupeKey?: string;
    method?: string;
    params?: unknown;
    url?: string;
}

/**
 * 首请求成功获取锁时返回的所有者句柄。
 */
export interface AcquiredWriteRequestLockHandle {
    acquired: true;
    key: string;
    ownerId: number;
}

/**
 * 相同请求已被锁定时返回的非所有者句柄。
 */
export interface RejectedWriteRequestLockHandle {
    acquired: false;
    key: string;
}

export type WriteRequestLockHandle = AcquiredWriteRequestLockHandle | RejectedWriteRequestLockHandle;

/**
 * 单条在途写请求的锁状态。
 */
interface PendingWriteRequest {
    maxTimerId: unknown;
    minTimerId?: unknown;
    ownerId: number;
    startedAt: number;
}

/**
 * 系统时钟适配器，集中封装 timer 类型差异。
 */
const systemClock: RequestWriteLockClock = {
    now: () => Date.now(),
    setTimeout: (callback, delay) => globalThis.setTimeout(callback, delay),
    clearTimeout: timerId => globalThis.clearTimeout(timerId as ReturnType<typeof globalThis.setTimeout>)
};

/**
 * 默认视为写操作的 HTTP 方法。
 */
const WRITE_REQUEST_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/**
 * 为无法同步读取内容的 File/Blob 分配稳定对象标识，避免仅凭元数据误判不同二进制内容。
 */
const binaryObjectIds = new WeakMap<object, number>();
let nextBinaryObjectId = 1;

/**
 * 同一二进制对象在多次指纹计算中复用标识，不同对象即使元数据相同也不会碰撞。
 */
const getBinaryObjectId = (value: object) => {
    let objectId = binaryObjectIds.get(value);

    if (objectId === undefined) {
        objectId = nextBinaryObjectId++;
        binaryObjectIds.set(value, objectId);
    }

    return objectId;
};

/**
 * 对键值对排序并序列化，保证插入顺序不同但内容相同的数据得到相同结果。
 */
const serializeEntries = (entries: Array<[string, unknown]>, ancestors: WeakSet<object>) => {
    return entries
        .map(([key, value]) => [key, stableSerialize(value, ancestors)] as const)
        .sort(([leftKey, leftValue], [rightKey, rightValue]) => {
            return leftKey.localeCompare(rightKey) || leftValue.localeCompare(rightValue);
        })
        .map(([key, value]) => `${JSON.stringify(key)}:${value}`)
        .join(",");
};

/**
 * 稳定序列化请求参数，显式区分类型并处理浏览器常用传输对象。
 */
const stableSerialize = (value: unknown, ancestors: WeakSet<object> = new WeakSet()): string => {
    if (value === null) return "null";
    if (value === undefined) return "undefined";

    const valueType = typeof value;

    if (valueType === "string") return `string:${JSON.stringify(value)}`;
    if (valueType === "boolean") return `boolean:${String(value)}`;
    if (valueType === "number") return `number:${String(value)}`;
    if (valueType === "bigint") return `bigint:${String(value)}`;

    if (value instanceof Date) return `date:${value.toISOString()}`;
    if (typeof URLSearchParams !== "undefined" && value instanceof URLSearchParams) {
        return `search:${serializeEntries([...value.entries()], ancestors)}`;
    }
    if (typeof File !== "undefined" && value instanceof File) {
        return `file:${JSON.stringify([value.name, value.size, value.type, value.lastModified, getBinaryObjectId(value)])}`;
    }
    if (typeof Blob !== "undefined" && value instanceof Blob) {
        return `blob:${JSON.stringify([value.size, value.type, getBinaryObjectId(value)])}`;
    }
    if (typeof FormData !== "undefined" && value instanceof FormData) {
        return `form:${serializeEntries([...value.entries()], ancestors)}`;
    }

    if (typeof value !== "object") return `${valueType}:${String(value)}`;
    if (ancestors.has(value)) throw new TypeError("请求防重参数不能包含循环引用");

    ancestors.add(value);

    let serializedValue: string;

    if (Array.isArray(value)) {
        // 数组顺序属于请求语义，不能像对象字段一样重新排序。
        serializedValue = `array:[${value.map(item => stableSerialize(item, ancestors)).join(",")}]`;
    } else {
        serializedValue = `object:{${serializeEntries(Object.entries(value), ancestors)}}`;
    }

    ancestors.delete(value);
    return serializedValue;
};

/**
 * 为默认启用防重的写请求生成稳定请求键；GET 和显式例外返回 null。
 */
export const createWriteRequestKey = (config: WriteRequestKeyConfig): string | null => {
    if (config.dedupe === false) return null;

    const method = (config.method ?? "GET").toUpperCase();

    if (!WRITE_REQUEST_METHODS.has(method)) return null;
    if (config.dedupeKey) return `custom:${config.dedupeKey}`;

    return [method, config.baseURL ?? "", config.url ?? "", stableSerialize(config.params), stableSerialize(config.data)].join(
        "&"
    );
};

/**
 * 创建按请求键管理的写请求锁。
 */
export function createWriteRequestLock(options: RequestWriteLockOptions = {}) {
    const clock = options.clock ?? systemClock;
    const minLockMs = options.minLockMs ?? 500;
    const maxLockMs = options.maxLockMs ?? 2000;
    const pendingMap = new Map<string, PendingWriteRequest>();
    let nextOwnerId = 1;

    /**
     * 仅当待释放句柄仍是当前锁所有者时删除记录。
     */
    const deleteIfOwner = (key: string, ownerId: number) => {
        const pendingRequest = pendingMap.get(key);

        if (!pendingRequest || pendingRequest.ownerId !== ownerId) return false;

        if (pendingRequest.minTimerId !== undefined) {
            clock.clearTimeout(pendingRequest.minTimerId);
        }
        clock.clearTimeout(pendingRequest.maxTimerId);
        pendingMap.delete(key);
        return true;
    };

    /**
     * 获取指定请求键的锁；已有锁时返回非所有者句柄。
     */
    const acquire = (key: string): WriteRequestLockHandle => {
        if (pendingMap.has(key)) {
            return { acquired: false, key };
        }

        const ownerId = nextOwnerId++;
        const pendingRequest: PendingWriteRequest = {
            maxTimerId: undefined,
            ownerId,
            startedAt: clock.now()
        };

        pendingMap.set(key, pendingRequest);
        // 最长锁定时间固定从首请求开始计算，重复请求不会延长锁时间。
        pendingRequest.maxTimerId = clock.setTimeout(() => {
            deleteIfOwner(key, ownerId);
        }, maxLockMs);

        return { acquired: true, key, ownerId };
    };

    /**
     * 处理服务器响应；快速响应需要等待最短锁定时间后再释放。
     */
    const release = (handle: WriteRequestLockHandle) => {
        if (!handle.acquired) return false;

        const pendingRequest = pendingMap.get(handle.key);

        if (!pendingRequest || pendingRequest.ownerId !== handle.ownerId) return false;

        const remainingMinLock = minLockMs - (clock.now() - pendingRequest.startedAt);

        if (remainingMinLock > 0) {
            pendingRequest.minTimerId ??= clock.setTimeout(() => {
                deleteIfOwner(handle.key, handle.ownerId);
            }, remainingMinLock);
            return true;
        }

        return deleteIfOwner(handle.key, handle.ownerId);
    };

    return {
        acquire,
        release
    };
}

/**
 * 共享适配器需要读写锁句柄的最小请求配置。
 */
export interface WriteRequestLockAdapterConfig extends WriteRequestKeyConfig {
    writeRequestLockHandle?: WriteRequestLockHandle;
}

/**
 * 共享适配器依赖的请求锁能力。
 */
export interface WriteRequestLock {
    acquire: (key: string) => WriteRequestLockHandle;
    release: (handle: WriteRequestLockHandle) => boolean;
}

/**
 * 端侧取消错误可由共享层附加稳定的重复请求标记。
 */
export interface DuplicateWriteRequestError extends Error {
    isDuplicateWriteRequest?: boolean;
}

/**
 * 共享适配器的端侧依赖；取消错误由调用端注入，避免工具包依赖传输库。
 */
export interface WriteRequestLockAdapterOptions<TConfig extends WriteRequestLockAdapterConfig> {
    createDuplicateError: (config: TConfig) => DuplicateWriteRequestError;
    createRequestKey: (config: TConfig) => string | null;
    requestLock: WriteRequestLock;
}

/**
 * 创建请求配置与共享写请求锁之间的通用适配器。
 */
export function createWriteRequestLockAdapter<TConfig extends WriteRequestLockAdapterConfig>(
    options: WriteRequestLockAdapterOptions<TConfig>
) {
    /**
     * 不需要防重的请求保持原配置，不产生锁状态。
     */
    const acquire = (config: TConfig) => {
        const requestKey = options.createRequestKey(config);

        if (!requestKey) return config;

        const lockHandle = options.requestLock.acquire(requestKey);
        config.writeRequestLockHandle = lockHandle;

        if (!lockHandle.acquired) {
            const duplicateError = options.createDuplicateError(config);
            duplicateError.isDuplicateWriteRequest = true;
            throw duplicateError;
        }

        return config;
    };

    /**
     * 仅识别由共享适配器标记的端侧重复请求错误。
     */
    const isDuplicateError = (error: unknown): boolean => {
        return (
            typeof error === "object" &&
            error !== null &&
            "isDuplicateWriteRequest" in error &&
            error.isDuplicateWriteRequest === true
        );
    };

    /**
     * 清除响应配置中的句柄，并由共享锁校验所有权后释放。
     */
    const release = (config?: TConfig) => {
        const lockHandle = config?.writeRequestLockHandle;

        if (!lockHandle) return false;

        delete config.writeRequestLockHandle;
        return options.requestLock.release(lockHandle);
    };

    return {
        acquire,
        isDuplicateError,
        release
    };
}
